import { CommonModule } from '@angular/common';
import {
  AfterViewChecked, Component, ElementRef, EventEmitter, Input, OnChanges,
  OnDestroy, Output, SimpleChanges, ViewChild, inject
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import { InputTextModule } from 'primeng/inputtext';
import { SelectModule } from 'primeng/select';
import { TooltipModule } from 'primeng/tooltip';
import { MessageService } from 'primeng/api';
import { Subscription, firstValueFrom, interval } from 'rxjs';

import { ConversationService } from '../../../service/core/ConversationService.service';
import { SubscriptionService } from '../../../service/core/subscription.service';
import { TimezoneService } from '../../../service/core/timezone.service';
import { convInitials, convSubtitle, convTitle } from '../../conversation-display.util';

/**
 * One customer's WhatsApp / widget message thread: history, media, the 24-hour window with its
 * template picker, the composer (text, attachments, voice notes) and delivery ticks.
 *
 * <p>Extracted from the Live Conversations page so the same thread can also run inside the global
 * chat dock. It owns everything below the header: its own polling, its own scroll behaviour and its
 * own window state, keyed entirely off {@link conversation}. The host supplies the conversation and
 * decides the chrome around it - the full inbox wraps it in three columns, the dock in a small
 * floating window.</p>
 *
 * <p>Changing {@link conversation} fully resets the thread, so a host may reuse one instance for
 * successive chats.</p>
 */
@Component({
  selector: 'app-chat-thread',
  standalone: true,
  imports: [
    CommonModule, FormsModule, ButtonModule, DialogModule,
    InputTextModule, SelectModule, TooltipModule
  ],
  templateUrl: './chat-thread.component.html',
  // The same literal stylesheet the inbox uses: emulated encapsulation scopes styles per component,
  // so the thread needs its own copy of the .conv-chat / .bubble / .tmpl-* rules to look identical.
  styleUrls: ['../../conversation.scss'],
  // A custom element is display:inline by default. The template's root .conv-chat is `flex: 1`,
  // which does nothing unless its PARENT - this host - is a flex container, so without this the
  // thread collapsed to zero height and both the inbox column and every dock window rendered blank.
  styles: [`
    :host { display: flex; flex-direction: column; flex: 1 1 auto; min-height: 0; min-width: 0; }

    /* min-height:0 is REQUIRED, not cosmetic. .conv-chat used to be a child of .conv-body, a flex
       ROW, where its height came from align-items:stretch. Inside this host it is a child of a flex
       COLUMN, and there flex:1 with the default min-height:auto refuses to shrink below the content
       height - so the message list grew instead of scrolling and pushed the composer off-screen.
       (.conv-chat__messages needs no such rule: overflow-y:auto already zeroes its automatic
       minimum size.) */
    :host > .conv-chat { min-height: 0; }
  `]
})
export class ChatThreadComponent implements OnChanges, AfterViewChecked, OnDestroy {

  /** The conversation to show. Null renders the empty state. Replaces the page's `selected`. */
  @Input() conversation: any = null;

  /** Hide the thread's own header row. The floating dock sets this: its window bar already
   *  shows the avatar, name, number and hand-over control, so the header underneath was drawing
   *  the same three things a second time and eating ~52px of a short window. The Live
   *  Conversations page leaves it false - there the header IS the only one. */
  @Input() compact = false;

  /** Raised after the agent flips AI <-> team, so a host list can refresh its badge. */
  @Output() handoffChanged = new EventEmitter<string>();

  private conversationService = inject(ConversationService);
  private messageService = inject(MessageService);
  private subscriptionService = inject(SubscriptionService);
  private tz = inject(TimezoneService);

  @ViewChild('chatContainer') private chatContainer!: ElementRef;

  /* Same labelling as the list and the dock - see conversation-display.util. Bound as fields
     because a template cannot call a bare imported function. */
  convTitle = convTitle;
  convSubtitle = convSubtitle;
  convInitials = convInitials;

  private pollingSub: Subscription | null = null;

  ngOnChanges(changes: SimpleChanges): void {
    if (!changes['conversation']) return;
    const prev = changes['conversation'].previousValue?.waId;
    const next = changes['conversation'].currentValue?.waId;
    if (prev === next) return;
    this.resetThread();
    if (!next) { this.stopPolling(); return; }
    this.loadMessages(true);   // a fresh thread always opens on the newest message
    this.checkWindow();
    this.startPolling();
  }

  ngOnDestroy(): void {
    this.stopPolling();
    clearInterval(this.recordTimer);
  }

  /** Clears every per-conversation field so a reused instance cannot show the previous chat. */
  private resetThread(): void {
    this.stopPolling();
    this.messages = [];
    this.hasEarlier = false;
    this.loadingEarlier = false;
    this.messageInput = '';
    this.pendingFiles = [];
    this.isUserAtBottom = true;
    this.windowOpen = true;
    this.windowChecked = false;
    this.windowLastInboundAt = null;
    this.showTemplateDialog = false;
    this.selectedTemplate = null;
    this.templateVars = [];
    this.headerFormatSel = null;
    this.headerMediaUrl = null;
    this.headerFileName = null;
  }

  /**
   * Consumes the two scroll intents. FORCE (a newly opened thread, or the agent's own message)
   * scrolls unconditionally and re-arms stickiness; the GUARDED path is the 5s poll, which must
   * leave someone reading older messages exactly where they are. The pin plus the two backstops
   * cover media that only sizes itself after this pass - an unloaded <img> contributes no height.
   */
  ngAfterViewChecked(): void {
    if (this.forceScrollToBottom) {
      this.forceScrollToBottom = false;
      this.needsScrollToBottom = false;
      this.isUserAtBottom = true;
      this.pinUntil = Date.now() + 1000;
      this.jumpToBottom();
      setTimeout(() => this.repinIfPinned(), 120);
      setTimeout(() => this.repinIfPinned(), 350);
    } else if (this.needsScrollToBottom) {
      this.needsScrollToBottom = false;
      this.scrollToBottom();
    }
  }

  /** Stable identity per message so the 5s poll (which reassigns a fresh array) doesn't destroy +
   *  recreate every bubble — which would reset in-progress <audio>/<video> playback and flicker
   *  images. Server rows carry a UUID id; the optimistic local echo falls back to createdAt/index. */
  trackMsg(index: number, msg: any): any {
    return msg?.id ?? msg?.messageId ?? msg?.createdAt ?? index;
  }

  /** Guarded scroll - the poll path. Never moves a reader who has scrolled up. */
  private scrollToBottom(): void {
    if (!this.isUserAtBottom) return;
    this.jumpToBottom();
  }

  /* ── Hand over to agent / back to AI ─────────────────────────────────────
   * Flips conversation_state.handoff_status — the flag the engine checks on every inbound.
   * ACTIVE: replies route to the team, the AI never answers. NONE: the AI resumes. */
  togglingHandoff = false;

  toggleHandoff(): void {
    if (!this.conversation?.waId || this.togglingHandoff) return;
    const activate = this.conversation.handoff !== 'ACTIVE';
    this.togglingHandoff = true;
    this.conversationService.setHandoff(this.conversation.waId, activate).subscribe({
      next: () => {
        this.togglingHandoff = false;
        this.conversation.handoff = activate ? 'ACTIVE' : null;
        // The host owns the conversation list; tell it rather than reaching into it. The inbox
        // mirrors this onto its row, and the chat dock onto its own.
        this.handoffChanged.emit(this.conversation.handoff);
        this.messageService.add({
          severity: 'success',
          summary: activate ? 'Handed over to agent' : 'Handed back to AI',
          detail: activate ? 'The AI will stop replying — this chat is now with the team.'
                           : 'The AI will handle this conversation again.'
        });
      },
      error: (err: any) => {
        this.togglingHandoff = false;
        const detail = err?.error?.message || err?.message || 'Could not update the conversation.';
        this.messageService.add({ severity: 'error', summary: 'Hand over failed', detail });
      }
    });
  }

  /* ── Chat ──────────────────────────────────────────── */
  messages: any[] = [];

  messageInput = '';

  /** Files staged in the composer — uploaded + sent as media messages on Send. */
  pendingFiles: { name: string; size: number; type: string; file: File }[] = [];

  /** Voice-note recorder state (composer mic button). */
  recording = false;

  recordSecs = 0;

  private recorder: MediaRecorder | null = null;

  private recordTimer: any = null;

  private recordChunks: Blob[] = [];

  conversationStatus = 'active';

  isUserAtBottom = true;
  /* Two DISTINCT intents, deliberately not one flag:
       - needsScrollToBottom: a poll refresh. Honours isUserAtBottom, so an agent reading older
         messages is never yanked down every 5 seconds.
       - forceScrollToBottom: a conversation switch or the user's own send. Must land on the newest
         message regardless of where the previous conversation was scrolled to.
     pinUntil keeps re-snapping while late media settles: a chat <img> has no reserved height, so
     the first scroll happens against an image-less layout and the real bottom moves down as each
     bitmap arrives. ignoreScrollUntil stops OUR programmatic jump from being read back as a user
     scroll - that feedback is what used to latch isUserAtBottom=false permanently, after which
     every later scroll silently did nothing. */

  private needsScrollToBottom = false;

  private forceScrollToBottom = false;

  private pinUntil = 0;

  private ignoreScrollUntil = 0;

  /* ── 24-hour window + template re-engagement ──────────────────────────────
   * WhatsApp only delivers free-form text within 24h of the customer's last message. Outside that
   * window Meta accepts a free-text send then silently fails it — so when the window is closed we
   * block the text box and let the agent pick an approved template instead. */
  windowOpen = true;                       // fail-open until checked, so there's no false "closed" flash

  windowChecked = false;

  windowLastInboundAt: string | null = null;

  showTemplateDialog = false;

  templates: any[] = [];

  templatesLoading = false;

  selectedTemplate: any = null;

  templateVars: { key: string; label: string; value: string }[] = [];   // key = "1" (positional) or "first_name" (named)

  sendingTemplate = false;

  // Media header (IMAGE/VIDEO/DOCUMENT) for templates that carry one
  headerFormatSel: string | null = null;

  headerMediaUrl: string | null = null;

  headerFileName: string | null = null;

  /** An upload is in flight - blocks Send so a template cannot go out without its header. */
  uploadingHeader = false;

  /** One page of history, used for the first load, the 5s poll and each scrollback step. */
  private static readonly PAGE = 50;

  /** True while a page older than the current oldest message may still exist on the server. */
  hasEarlier = false;
  loadingEarlier = false;

  private msgLoadSeq = 0;

  loadMessages(force = false): void {
    if (!this.conversation?.waId) return;
    const seq = ++this.msgLoadSeq;
    const waId = this.conversation.waId;
    // Phone-keyed paged history (waId only) so messages load for BOTH WhatsApp and web-widget
    // chats. The old phoneNumberId+channel call early-returned when phoneNumberId was missing and
    // derived the channel from an async value — which left the chat window blank.
    this.conversationService
      .getConversationMessagesByPhone(this.conversation.waId, { limit: ChatThreadComponent.PAGE })
      .subscribe({
        next: (res: any) => {
          // Stale answer for a conversation the user has already moved off - drop it.
          if (seq !== this.msgLoadSeq || this.conversation?.waId !== waId) return;
          const batch: any[] = Array.isArray(res) ? res : (res?.data ?? []);
          if (!this.messages.length) {
            this.messages = batch;
            // A full page back means there is almost certainly more behind it. resetThread() clears
            // this on a conversation switch, so it only ever describes the chat on screen.
            this.hasEarlier = batch.length >= ChatThreadComponent.PAGE;
          } else {
            this.messages = this.mergeKeepingEarlier(batch);
          }
          if (force) this.forceScrollToBottom = true; else this.needsScrollToBottom = true;
          this.refreshWindowFromMessages();   // keep the 24h window state fresh as the chat polls
        },
        // A failed POLL must not blank a thread that is already on screen - one dropped request
        // would wipe the agent's scrollback and leave them staring at an empty chat until the
        // next tick. Only the first load, which has nothing to preserve, clears.
        error: () => { if (seq === this.msgLoadSeq && !this.messages.length) this.messages = []; }
      });
  }

  /**
   * Fold the newest page into what is already on screen WITHOUT dropping scrollback.
   *
   * <p>This call only ever returns the newest page. Assigning it straight to {@code messages} — as
   * this did — silently threw away any history the agent had pulled in with "Load earlier": the
   * thread rewound itself within 5 seconds, and it did the same the moment they sent a reply. So
   * keep everything that sits BEFORE the page we just fetched, and let the page itself be
   * authoritative for everything after it, so status changes and edits still land. Optimistic local
   * echoes have no id and are newer than the page, so they fall away here exactly as before.</p>
   */
  private mergeKeepingEarlier(batch: any[]): any[] {
    if (!batch.length) return this.messages;
    const oldestInBatch = this.timeOf(batch[0]);          // the API returns a page oldest-first
    const batchIds = new Set(batch.map(m => m?.id).filter(Boolean));
    const earlier = this.messages.filter(
      m => m?.id && !batchIds.has(m.id) && this.timeOf(m) <= oldestInBatch);
    return [...earlier, ...batch];
  }

  private timeOf(m: any): number {
    const t = Date.parse(m?.createdAt ?? m?.created_at ?? '');
    return Number.isNaN(t) ? 0 : t;
  }

  /**
   * Scrollback — prepend the page older than the current oldest message.
   *
   * <p>Without this the thread showed the newest 50 and nothing else, with no way back: an 82
   * message conversation opened in the middle of yesterday and the agent could not see that a
   * colleague had already answered, or that the customer arrived from a campaign.</p>
   */
  loadEarlierMessages(): void {
    const waId = this.conversation?.waId;
    if (!waId || this.loadingEarlier || !this.messages.length) return;
    const firstId = this.messages[0]?.id;
    if (!firstId) return;
    const el = this.chatContainer?.nativeElement;
    const heightBefore = el ? el.scrollHeight : 0;
    this.loadingEarlier = true;
    this.conversationService
      .getConversationMessagesByPhone(waId, { beforeId: String(firstId), limit: ChatThreadComponent.PAGE })
      .subscribe({
        next: (res: any) => {
          if (this.conversation?.waId !== waId) return;   // they switched chats mid-flight
          const batch: any[] = Array.isArray(res) ? res : (res?.data ?? []);
          const known = new Set(this.messages.map((m: any) => m?.id).filter(Boolean));
          const older = batch.filter((m: any) => m?.id && !known.has(m.id));
          if (older.length) this.messages = [...older, ...this.messages];
          this.hasEarlier = batch.length >= ChatThreadComponent.PAGE;
          this.loadingEarlier = false;
          this.holdPositionAfterPrepend(heightBefore);
        },
        error: () => { this.loadingEarlier = false; }
      });
  }

  /**
   * Keep the agent looking at the same message after older ones are inserted above it.
   *
   * <p>Growing the content above the viewport moves everything down by exactly the height added, so
   * adding that back to scrollTop leaves the reader where they were instead of hurling them into
   * the middle of the history they just asked for.</p>
   */
  private holdPositionAfterPrepend(heightBefore: number): void {
    setTimeout(() => {
      const el = this.chatContainer?.nativeElement;
      if (!el) return;
      const added = el.scrollHeight - heightBefore;
      if (added > 0) {
        this.ignoreScrollUntil = Date.now() + 150;   // our own jump, not the reader scrolling
        el.scrollTop = el.scrollTop + added;
      }
    });
  }

  sendMessage(): void {
    const text = this.messageInput.trim();
    if (this.isFreePlan || (!text && !this.pendingFiles.length) || !this.conversation?.waId) return;
    // Closed 24h window: free text won't be delivered — send a template instead. (The server /send
    // endpoint also enforces this, so a doomed free-text send never leaves regardless of UI state.)
    if (this.isWhatsApp() && this.windowChecked && !this.windowOpen) {
      this.openTemplatePicker();
      return;
    }
    // Staged files: upload each, send each as its own media message — the typed text rides the
    // first one as its caption. Text-only keeps the old optimistic-echo path.
    if (this.pendingFiles.length) {
      this.sendPendingFiles(text);
      return;
    }
    // Optimistic bubble so it shows instantly; the poll/refresh reconciles with the server.
    this.messages.push({
      type: 'agent',
      direction: 'OUTBOUND',
      content: { text },
      createdAt: new Date().toISOString(),
      sender: 'You'
    });
    this.messageInput = '';
    this.forceScrollToBottom = true;   // your own message always pulls the view down
    // Actually send it (WhatsApp). The previous code only appended locally — nothing ever left the
    // browser, which is why replies "did nothing". Refresh from the server on success.
    this.conversationService.sendMessage({ waId: this.conversation.waId, message: text }).subscribe({
      next: () => this.loadMessages(true),
      // Show the SERVER's reason (e.g. "the 24-hour window is closed — send a template"), not a
      // generic retry message the agent can't act on. A closed window also flips the composer into
      // template mode so the next attempt goes down the right path.
      error: (err: any) => {
        const detail = err?.error?.message || err?.message || 'Could not send the message. Please try again.';
        this.messageService.add({ severity: 'error', summary: 'Send failed', detail, life: 6000 });
        if (err?.status === 409) {
          this.windowOpen = false;
          this.windowChecked = true;
        }
      }
    });
  }

  onComposerAttach(event: Event): void {
    const input = event.target as HTMLInputElement;
    for (const f of Array.from(input.files || [])) {
      this.pendingFiles.push({ name: f.name, size: f.size, type: f.type || '', file: f });
    }
    input.value = '';
  }

  removePendingFile(i: number): void {
    this.pendingFiles.splice(i, 1);
  }

  /** WhatsApp media kind from a file's MIME type. */
  private kindFor(mime: string): 'IMAGE' | 'VIDEO' | 'AUDIO' | 'DOCUMENT' {
    const m = (mime || '').toLowerCase();
    if (m.startsWith('image/')) return 'IMAGE';
    if (m.startsWith('video/')) return 'VIDEO';
    if (m.startsWith('audio/')) return 'AUDIO';
    return 'DOCUMENT';
  }

  private sendPendingFiles(caption: string): void {
    const waId = this.conversation?.waId;
    if (!waId) return;
    const files = this.pendingFiles.slice();
    this.pendingFiles = [];
    this.messageInput = '';
    const run = async () => {
      let first = true;
      for (const f of files) {
        try {
          const up: any = await firstValueFrom(this.conversationService.uploadTemplateHeaderMedia(f.file));
          const url = up?.data?.url ?? up?.url;
          if (!url) throw new Error('upload failed');
          await firstValueFrom(this.conversationService.sendMessage({
            waId, message: first ? caption : '', kind: this.kindFor(f.type), mediaUrl: url, filename: f.name
          }));
          first = false;
        } catch (err: any) {
          const detail = err?.error?.message || 'Could not send ' + f.name + '.';
          this.messageService.add({ severity: 'error', summary: 'Send failed', detail, life: 6000 });
          if (err?.status === 409) { this.windowOpen = false; this.windowChecked = true; break; }
        }
      }
      this.loadMessages(true);   // your own attachment pulls the view down when it lands
    };
    run();
  }

  /** Mic button: start recording, or stop-and-send the voice note. Prefers audio/mp4 —
   *  Meta rejects webm audio, so webm recordings (old browsers) may show as not delivered. */
  toggleRecording(): void {
    if (this.recording) { this.recorder?.stop(); return; }
    if (!('MediaRecorder' in window)) {
      this.messageService.add({ severity: 'warn', summary: 'Not supported', detail: 'This browser cannot record audio.' });
      return;
    }
    navigator.mediaDevices.getUserMedia({ audio: true }).then(stream => {
      const mime = MediaRecorder.isTypeSupported('audio/mp4') ? 'audio/mp4'
        : (MediaRecorder.isTypeSupported('audio/webm;codecs=opus') ? 'audio/webm;codecs=opus' : '');
      const rec = mime ? new MediaRecorder(stream, { mimeType: mime }) : new MediaRecorder(stream);
      this.recorder = rec;
      this.recordChunks = [];
      this.recording = true;
      this.recordSecs = 0;
      this.recordTimer = setInterval(() => this.recordSecs++, 1000);
      rec.ondataavailable = ev => { if (ev.data?.size) this.recordChunks.push(ev.data); };
      rec.onstop = () => {
        clearInterval(this.recordTimer);
        this.recording = false;
        stream.getTracks().forEach(t => t.stop());
        const type = rec.mimeType || 'audio/mp4';
        const blob = new Blob(this.recordChunks, { type });
        this.recordChunks = [];
        if (blob.size < 1000) return;   // a tap, not a recording
        const ext = type.includes('mp4') ? 'm4a' : 'webm';
        this.sendVoiceNote(new File([blob], 'voice-note.' + ext, { type }));
      };
      rec.start();
    }).catch(() => {
      this.messageService.add({ severity: 'error', summary: 'Microphone', detail: 'Microphone access was denied.' });
    });
  }

  private sendVoiceNote(file: File): void {
    const waId = this.conversation?.waId;
    if (!waId) return;
    const run = async () => {
      try {
        const up: any = await firstValueFrom(this.conversationService.uploadTemplateHeaderMedia(file));
        const url = up?.data?.url ?? up?.url;
        if (!url) throw new Error('upload failed');
        await firstValueFrom(this.conversationService.sendMessage({
          waId, kind: 'AUDIO', mediaUrl: url, filename: file.name, voice: true
        }));
        this.loadMessages(true);   // ditto for a voice note
      } catch (err: any) {
        const detail = err?.error?.message || 'Could not send the voice note.';
        this.messageService.add({ severity: 'error', summary: 'Send failed', detail, life: 6000 });
        if (err?.status === 409) { this.windowOpen = false; this.windowChecked = true; }
      }
    };
    run();
  }

  onQuickReply(btn: any): void {
    if (this.isFreePlan) return;
    const displayText = btn.text ?? btn.label ?? btn.value ?? '';
    const sendValue   = btn.id   ?? btn.value ?? '';
    this.messages.push({
      direction: 'INBOUND',
      content: { text: displayText },
      createdAt: new Date().toISOString()
    });
    this.conversationService.sendMessage({
      waId: this.conversation.waId,
      message: sendValue,
      role: 'USER'
    }).subscribe();
  }

  onScroll(): void {
    // Our own jump queues a scroll event one frame later; reading it back would mis-measure the
    // position against a layout that is still growing and latch isUserAtBottom=false for good.
    if (Date.now() < this.ignoreScrollUntil) return;
    const el = this.chatContainer?.nativeElement;
    if (!el) return;
    this.isUserAtBottom = el.scrollTop + el.clientHeight >= el.scrollHeight - 100;
    if (!this.isUserAtBottom) this.pinUntil = 0;
    // Reaching the top IS the request for older messages - the button above it is only for agents
    // who never think to try. Our own restore-jump is filtered by the ignore window above, so this
    // cannot chain-fire off the prepend it just caused.
    if (el.scrollTop <= 80 && this.hasEarlier && !this.loadingEarlier) this.loadEarlierMessages();
  }

  /** Real input intent - the agent grabbed the scrollbar/wheel, so stop re-pinning immediately.
   *  Bound separately from (scroll) because the ignore window above filters scroll events. */
  cancelPin(): void {
    this.pinUntil = 0;
    this.ignoreScrollUntil = 0;
  }

  /** A media element finished laying out. While pinned, the bottom just moved - follow it. */
  onChatMediaLoaded(): void {
    this.repinIfPinned();
  }

  private repinIfPinned(): void {
    if (Date.now() < this.pinUntil || this.isUserAtBottom) this.jumpToBottom();
  }

  /** UNCONDITIONAL scroll - ignores isUserAtBottom. For opening a chat and for the user's own send. */
  private jumpToBottom(): void {
    const el = this.chatContainer?.nativeElement;
    if (!el) return;
    try {
      this.ignoreScrollUntil = Date.now() + 150;
      el.scrollTop = el.scrollHeight;
      this.isUserAtBottom = true;
    } catch { /* silent */ }
  }

  private startPolling(): void {
    this.stopPolling();
    this.pollingSub = interval(5000).subscribe(() => this.loadMessages());
  }

  private stopPolling(): void {
    this.pollingSub?.unsubscribe();
  }

  getMessageText(msg: any): string | null {
    let text = msg?.content?.text;
    if (!text) return null;
    text = String(text).trim();
    // Strip the "[Image]"/"[Video]"/… placeholder the backend prepends to a media caption — but ONLY
    // when the bubble actually renders that media. For an outbound media row with no URL (e.g. audio /
    // sticker), keep the "[Audio]" text so it shows a friendly preview instead of "Unsupported message".
    if (this.mediaSrc(msg)) {
      text = text.replace(/^\[(Image|Video|Audio|Voice|Document|Location|Sticker|Contact|Carousel)\]\s*/i, '');
      // The ingest fallback placeholders are whole-message brackets in the tenant's language
      // (e.g. "[वॉइस मैसेज प्राप्त हुआ]") — noise once the actual media renders.
      if (/^\[[^\]]+\]$/.test(text.trim())) text = '';
    }
    return text.trim() ? text.trim() : null;
  }

  /** The URL to render for a media message (direct, public). */
  mediaSrc(msg: any): string | null {
    return msg?.content?.mediaUrl || msg?.content?.image || null;
  }

  /** Open a full-size image in a new tab (WhatsApp-style tap-to-view). */
  openImage(msg: any): void {
    const src = this.mediaSrc(msg);
    if (src) window.open(src, '_blank', 'noopener');
  }

  /** Google-Maps link for a shared location pin. */
  mapUrl(msg: any): string {
    const lat = msg?.content?.latitude, lng = msg?.content?.longitude;
    return (lat != null && lng != null)
      ? `https://www.google.com/maps/search/?api=1&query=${lat},${lng}` : '#';
  }

  /** A message with nothing renderable (no text, no media, no location, no buttons). */
  isEmptyMessage(msg: any): boolean {
    const c = msg?.content;
    if (!c) return true;
    if (this.getMessageText(msg)) return false;
    if (c.mediaUrl || c.image) return false;
    if (c.kind === 'LOCATION' && c.latitude != null) return false;
    if (c.buttons?.length) return false;
    return true;
  }

  /** A message Meta could not deliver — surfaced so the agent doesn't think a failed reply was sent. */
  isFailed(msg: any): boolean {
    return String(msg?.status).toUpperCase() === 'FAILED';
  }

  /**
   * Meta's own words for why a message did not arrive.
   *
   * Null for every message that failed before 2026-09-12: the reason was logged on the
   * server and never stored, so those rows genuinely have nothing to show. The bubble
   * then falls back to the bare "not delivered" tag, which is the honest answer.
   *
   * Deliberately shows the raw text rather than mapping codes to friendly phrasing.
   * A wrong friendly label is worse than Meta's clumsy one, and the code is in the
   * tooltip for anyone who wants to look it up.
   */
  failureText(msg: any): string | null {
    const f = msg?.failure;
    if (!f) return null;
    const t = (f.title || f.message || f.details || '').toString().trim();
    return t.length ? t : null;
  }

  /** Code and timestamp, for the hover. Kept out of the bubble to avoid noise. */
  failureTooltip(msg: any): string {
    const f = msg?.failure;
    if (!f) return 'Not delivered. No reason was recorded for this message.';
    const bits = [
      f.code ? `Meta error ${f.code}` : null,
      (f.message || f.title || '').toString().trim() || null,
      f.details && f.details !== f.message && f.details !== f.title ? f.details : null,
    ].filter(Boolean);
    return bits.join(' — ') || 'Not delivered.';
  }

  /** WhatsApp-style receipt state from Meta's status webhooks: 'sent' (✓, incl. PENDING —
   *  an optimistic local echo has no status yet), 'delivered' (✓✓), 'read' (blue ✓✓). */
  tickState(msg: any): 'sent' | 'delivered' | 'read' {
    switch (String(msg?.status).toUpperCase()) {
      case 'READ': return 'read';
      case 'DELIVERED': return 'delivered';
      default: return 'sent';
    }
  }

  tickTitle(msg: any): string {
    switch (this.tickState(msg)) {
      case 'read': return 'Read';
      case 'delivered': return 'Delivered';
      default: return 'Sent';
    }
  }

  /* ── 24-hour window ────────────────────────────────────────────────────────── */
  private isWhatsApp(): boolean {
    return String(this.conversation?.channel || 'WHATSAPP').toUpperCase() === 'WHATSAPP';
  }

  private checkWindow(): void {
    const waId = this.conversation?.waId;
    if (!waId) return;
    // The 24h window is a WhatsApp-only rule — never gate web-widget (or other) channels on it.
    if (!this.isWhatsApp()) {
      this.windowOpen = true; this.windowChecked = false; this.windowLastInboundAt = null;
      return;
    }
    this.windowChecked = false;
    this.conversationService.getWhatsAppWindow(waId).subscribe({
      next: (res: any) => {
        const d = res?.data ?? res ?? {};
        this.windowOpen = !!d.open;
        this.windowLastInboundAt = d.lastInboundAt ?? null;
        this.windowChecked = true;
      },
      // Fail-open — a check error must not block the agent from replying. The server-side /send guard
      // still rejects a genuinely out-of-window free-text send, so this can't cause a silent failure.
      error: () => { this.windowOpen = true; this.windowChecked = true; }
    });
  }

  /** Keep the window fresh while the chat stays open (no extra API call): a new inbound reopens it,
   *  time passing closes it. Uses the already-loaded messages; WhatsApp only. */
  private refreshWindowFromMessages(): void {
    if (!this.isWhatsApp()) return;
    let latest: number | null = null;
    for (const m of this.messages) {
      if (m?.direction === 'INBOUND' && m?.createdAt) {
        const t = new Date(m.createdAt).getTime();
        if (t && (latest == null || t > latest)) latest = t;
      }
    }
    if (latest == null) return;   // no inbound in the loaded page — keep the authoritative API result
    if (!this.windowLastInboundAt || latest > new Date(this.windowLastInboundAt).getTime()) {
      this.windowLastInboundAt = new Date(latest).toISOString();
    }
    this.windowOpen = (Date.now() - latest) < 24 * 3_600_000;
    this.windowChecked = true;
  }

  /** "3d ago" style label for when the customer last replied (closed-window banner). */
  get windowClosedSince(): string {
    if (!this.windowLastInboundAt) return '';
    const then = new Date(this.windowLastInboundAt).getTime();
    if (!then) return '';
    const hrs = Math.floor((Date.now() - then) / 3_600_000);
    return hrs < 24 ? `${hrs}h ago` : `${Math.floor(hrs / 24)}d ago`;
  }

  /* ── Template picker (closed-window re-engagement) ───────────────────────────── */
  openTemplatePicker(): void {
    this.showTemplateDialog = true;
    this.selectedTemplate = null;
    this.templateVars = [];
    this.templateUrlButtonValue = '';
    this.headerFormatSel = null;
    this.headerMediaUrl = null;
    this.headerFileName = null;
    this.uploadingHeader = false;
    if (!this.templates.length) this.loadTemplates();
  }

  private loadTemplates(): void {
    this.templatesLoading = true;
    this.conversationService.getWhatsAppTemplates().subscribe({
      next: (res: any) => {
        const rows: any[] = res?.data ?? res ?? [];
        // Only APPROVED templates can actually be delivered by Meta.
        this.templates = rows.filter(t => String(t?.status).toUpperCase() === 'APPROVED');
        this.templatesLoading = false;
      },
      error: () => { this.templates = []; this.templatesLoading = false; }
    });
  }

  selectTemplate(t: any): void {
    this.selectedTemplate = t;
    this.headerMediaUrl = null;
    this.headerFileName = null;
    this.templateUrlButtonValue = '';
    this.headerFormatSel = this.headerFormat(t);
    // One input per body variable — positional ({{1}}) or named ({{first_name}}).
    this.templateVars = this.bodyVarKeys(t).map(k => ({
      key: k,
      label: /^\d+$/.test(k) ? ('Variable #' + k) : k.replace(/_/g, ' '),
      value: ''
    }));
  }

  /** Ordered body variable keys: positional templates sorted 1..n; named templates in appearance order. */
  bodyVarKeys(t: any): string[] {
    const body = this.bodyText(t);
    const re = /\{\{\s*([A-Za-z0-9_]+)\s*\}\}/g;
    const seen: string[] = [];
    let m: RegExpExecArray | null;
    while ((m = re.exec(body)) !== null) {
      const k = m[1].trim();
      if (k && !seen.includes(k)) seen.push(k);
    }
    return seen.every(k => /^\d+$/.test(k)) ? seen.sort((a, b) => parseInt(a) - parseInt(b)) : seen;
  }

  /** The template's BODY text (for preview + variable detection). */
  bodyText(t: any): string {
    const body = this.componentsOf(t).find((c: any) => String(c?.type).toUpperCase() === 'BODY');
    return body?.text ?? '';
  }

  /** The template header format: TEXT | IMAGE | VIDEO | DOCUMENT | null. */
  headerFormat(t: any): string | null {
    const header = this.componentsOf(t).find((c: any) => String(c?.type).toUpperCase() === 'HEADER');
    if (!header) return null;
    return String(header?.format ?? '').toUpperCase() || null;
  }

  /** True when the template's header is media (needs an uploaded image/video/document). */
  needsHeaderMedia(t: any): boolean {
    const f = this.headerFormat(t);
    return f === 'IMAGE' || f === 'VIDEO' || f === 'DOCUMENT';
  }

  /** accept="" filter for the header file input, matching the header format. */
  headerAccept(): string {
    switch (this.headerFormatSel) {
      case 'IMAGE': return 'image/*';
      case 'VIDEO': return 'video/*';
      case 'DOCUMENT': return '.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx';
      default: return '*/*';
    }
  }

  onHeaderFile(event: any): void {
    const file: File | undefined = event?.target?.files?.[0];
    if (!file) return;
    this.uploadingHeader = true;
    this.conversationService.uploadTemplateHeaderMedia(file).subscribe({
      next: (res: any) => {
        const d = res?.data ?? res ?? {};
        this.headerMediaUrl = d.url ?? null;
        this.headerFileName = d.fileName || file.name;
        this.uploadingHeader = false;
        if (!this.headerMediaUrl) {
          this.messageService.add({ severity: 'error', summary: 'Upload failed', detail: 'No URL returned for the header media.' });
        }
      },
      error: () => {
        this.uploadingHeader = false;
        this.messageService.add({ severity: 'error', summary: 'Upload failed', detail: 'Could not upload the header media.' });
      }
    });
  }

  /** Value typed for the template's dynamic URL button ({{1}} in the button url). */
  templateUrlButtonValue = '';

  /** Does the template carry a URL button with a {{n}} variable in its url? Such a button's
   *  parameter is REQUIRED at send time (the server degrades a blank to a space). */
  templateHasDynamicUrlButton(t: any): boolean {
    if (!t) return false;
    for (const c of this.componentsOf(t)) {
      if (!c || String(c.type).toUpperCase() !== 'BUTTONS' || !Array.isArray(c.buttons)) continue;
      for (const b of c.buttons) {
        if (b && String(b.type).toUpperCase() === 'URL'
            && typeof b.url === 'string' && b.url.includes('{{')) return true;
      }
    }
    return false;
  }

  private componentsOf(t: any): any[] {
    const c = t?.components;
    if (Array.isArray(c)) return c;
    if (typeof c === 'string') {
      try { const p = JSON.parse(c); return Array.isArray(p) ? p : (p?.components ?? []); } catch { return []; }
    }
    return c?.components ?? [];
  }

  /** Body preview with the agent's values substituted for each {{key}}. */
  templatePreview(): string {
    let text = this.bodyText(this.selectedTemplate);
    for (const v of this.templateVars) {
      const esc = v.key.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
      text = text.replace(new RegExp('\\{\\{\\s*' + esc + '\\s*\\}\\}', 'g'), v.value || `{{${v.key}}}`);
    }
    return text;
  }

  get canSendTemplate(): boolean {
    if (!this.selectedTemplate || this.sendingTemplate || this.uploadingHeader) return false;
    if (this.needsHeaderMedia(this.selectedTemplate) && !this.headerMediaUrl) return false;
    return this.templateVars.every(v => v.value.trim().length > 0);
  }

  sendSelectedTemplate(): void {
    if (!this.canSendTemplate || !this.conversation?.waId) return;
    const bodyParameters: Record<string, string> = {};
    for (const v of this.templateVars) bodyParameters[v.key] = v.value.trim();
    const payload: any = {
      waId: this.conversation.waId,
      templateId: this.selectedTemplate.id,
      bodyParameters
    };
    if (this.needsHeaderMedia(this.selectedTemplate) && this.headerMediaUrl) {
      payload.headerMediaUrl = this.headerMediaUrl;
      payload.headerType = this.headerFormatSel;
      if (this.headerFileName) payload.headerFileName = this.headerFileName;
    }
    if (this.templateHasDynamicUrlButton(this.selectedTemplate) && this.templateUrlButtonValue.trim()) {
      payload.urlButtonParam = this.templateUrlButtonValue.trim();
    }
    this.sendingTemplate = true;
    this.conversationService.sendTemplate(payload).subscribe({
      next: () => {
        this.sendingTemplate = false;
        this.showTemplateDialog = false;
        this.messageService.add({ severity: 'success', summary: 'Template sent', detail: 'The template message was sent.' });
        this.loadMessages(true);
      },
      error: (err: any) => {
        this.sendingTemplate = false;
        this.messageService.add({ severity: 'error', summary: 'Send failed',
          detail: err?.message || 'Could not send the template. Please try again.' });
      }
    });
  }

  getMessageTime(msg: any): string {
    const raw = msg?.createdAt || msg?.sentAt || msg?.timestamp || msg?.time;
    if (!raw) return '';
    return this.tz.format(raw, 'time');
  }

  get isFreePlan(): boolean {
    return this.subscriptionService.isFreePlan();
  }
}
